/**
 * Orbs de Essence no mundo: inimigo morre → dropa orbs no chão → player se aproxima
 * → magnetismo (voam até ele) → coleta (soma no inventário + feedback). Orbs pooled.
 */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import type { EssenceDrop } from "../config/GameConfig";
import { events } from "../core/Events";
import { Pool } from "../utils/Pool";
import { type EssenceType, ESSENCE_COLOR } from "./EssenceTypes";
import type { EssenceInventory } from "./EssenceInventory";
import { Vip } from "../progression/Vip";

interface Orb {
  mesh: THREE.Mesh;
  type: EssenceType;
  amount: number;
  life: number;
  bob: number;
  magnet: boolean;
}

export class EssenceManager {
  private readonly cfg = GameConfig.essence;
  private readonly orbs: Orb[] = [];
  private readonly geo = new THREE.SphereGeometry(0.16, 8, 8);
  private readonly mats: Record<EssenceType, THREE.MeshStandardMaterial>;
  private readonly pool: Pool<THREE.Mesh>;
  private readonly tmp = new THREE.Vector3();
  reachMult = 1; // 10.5C: pet amplia o alcance de coleta/ímã de essência (nível do pet, ver GameProgress.petMagnetMult)

  constructor(
    private readonly scene: THREE.Scene,
    private readonly inventory: EssenceInventory,
  ) {
    this.mats = {} as Record<EssenceType, THREE.MeshStandardMaterial>;
    for (const type of Object.keys(ESSENCE_COLOR) as EssenceType[]) {
      this.mats[type] = new THREE.MeshStandardMaterial({
        color: ESSENCE_COLOR[type],
        emissive: ESSENCE_COLOR[type],
        emissiveIntensity: 1.4,
        roughness: 0.4,
      });
    }
    this.pool = new Pool<THREE.Mesh>(
      () => {
        const m = new THREE.Mesh(this.geo, this.mats.power);
        m.visible = false;
        this.scene.add(m);
        return m;
      },
      (m) => {
        m.visible = false;
      },
      8,
    );
    events.on("enemy:killed", (p) => this.onKill(p.drops, p.position));
  }

  private onKill(drops: EssenceDrop[], position: THREE.Vector3): void {
    for (const d of drops) {
      if (Math.random() > d.chance) continue;
      const amount = d.min === d.max ? d.min : d.min + Math.floor(Math.random() * (d.max - d.min + 1));
      if (amount > 0) this.spawnOrb(d.type, amount, position);
    }
  }

  private spawnOrb(type: EssenceType, amount: number, position: THREE.Vector3): void {
    const mesh = this.pool.acquire();
    mesh.material = this.mats[type];
    mesh.visible = true;
    mesh.position.set(
      position.x + (Math.random() - 0.5) * 0.8,
      this.cfg.orbFloatHeight,
      position.z + (Math.random() - 0.5) * 0.8,
    );
    this.orbs.push({ mesh, type, amount, life: this.cfg.orbLifetime, bob: Math.random() * Math.PI * 2, magnet: false });
  }

  /**
   * `pullBlocked(x,z)` (opcional): se true p/ um orb, magnetismo e coleta ficam
   * desativados (a essência não atravessa a barreira da Sanctuary).
   * `petPos` (10.9L21, opcional): o pet também puxa/coleta essência pela posição DELE, não só
   * do jogador — antes só o alcance do JOGADOR importava (o pet nunca ia atrás de nada sozinho).
   */
  update(dt: number, playerPos: THREE.Vector3, pullBlocked?: (x: number, z: number) => boolean, petPos?: THREE.Vector3 | null): void {
    for (let i = this.orbs.length - 1; i >= 0; i--) {
      const o = this.orbs[i];
      const p = o.mesh.position;
      this.tmp.set(playerPos.x - p.x, 0, playerPos.z - p.z);
      const distPlayer = this.tmp.length();
      let pullX = this.tmp.x, pullZ = this.tmp.z, dist = distPlayer;
      if (petPos) {
        const dpx = petPos.x - p.x, dpz = petPos.z - p.z;
        const distPet = Math.hypot(dpx, dpz);
        if (distPet < dist) { dist = distPet; pullX = dpx; pullZ = dpz; }
      }

      const blocked = pullBlocked?.(p.x, p.z) ?? false;

      if (!blocked && dist <= this.cfg.collectRadius * this.reachMult) {
        this.collect(o);
        this.orbs.splice(i, 1);
        continue;
      }
      o.life -= dt;
      if (o.life <= 0) {
        this.pool.release(o.mesh);
        this.orbs.splice(i, 1);
        continue;
      }

      if (blocked) {
        // congela na borda (só flutua) — não é puxado para dentro da safe zone
        o.bob += dt * 3;
        p.y = this.cfg.orbFloatHeight + Math.sin(o.bob) * 0.12;
        o.mesh.rotation.y += dt * 2;
        continue;
      }

      if (dist <= this.cfg.magnetRadius * this.reachMult) o.magnet = true;
      if (o.magnet && dist > 0.001) {
        const step = this.cfg.magnetSpeed * dt / dist;
        p.x += pullX * step;
        p.z += pullZ * step;
        p.y = this.cfg.orbFloatHeight;
      } else {
        o.bob += dt * 3;
        p.y = this.cfg.orbFloatHeight + Math.sin(o.bob) * 0.12;
      }
      o.mesh.rotation.y += dt * 2;
    }
  }

  /** 10.9L21 — orb vivo mais perto de `from` dentro de `maxDist` (o pet mira nele pra ir buscar). */
  nearestOrbPos(from: THREE.Vector3, maxDist: number): THREE.Vector3 | null {
    let best: THREE.Vector3 | null = null;
    let bd = maxDist;
    for (const o of this.orbs) {
      const d = Math.hypot(o.mesh.position.x - from.x, o.mesh.position.z - from.z);
      if (d < bd) { bd = d; best = o.mesh.position; }
    }
    return best;
  }

  private collect(o: Orb): void {
    // VIP (10.4Q): +% de essência coletada (regalia de conteúdo).
    const amount = Math.max(1, Math.round(o.amount * Vip.essenceMult));
    this.inventory.add(o.type, amount);
    events.emit("essence:collected", { type: o.type, amount });
    this.pool.release(o.mesh);
  }

  clear(): void {
    for (const o of this.orbs) this.pool.release(o.mesh);
    this.orbs.length = 0;
  }
}
